import React, { useEffect, useMemo, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { BillActions, paidStatusToIcon, documentStatusToIcon, documentTypeToIcon, paidStatusToColor, insuranceClaimStatusToIcon, insuranceClaimStatusToColor } from '.';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { formatCurrency, fullDateFromPostgresString, patientFullName, stringDatePostgresToDate } from '../../../utils/index.jsx';
import SearchBox, { FilterItem } from '../../../components/general/SearchBox';
import { BillStatus } from '../Request/types';
import { Grid, Paper, Typography, TextField, FormControl, Button, InputLabel, Select, MenuItem, CircularProgress } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import { ColourButton } from '../../../components/general/mini_components-ts';
import { ChipContainer } from '../Request/RequestInfo';
import { ColourChip } from '../../../components/general/mini_components-ts';
import { DocumentStatus, DocumentType } from './types';
import { HOSPITAL_BILLING_VIEW_DOCUMENT } from '../../../routes/urls';
import { useHistory } from 'react-router-dom';
import { documentStatusToColor, documentTypeToColor } from '../../../utils/bill';
import { IInsurance, ReduxStore } from '../../../constants/types';
import { LocalizeContextProps } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { InsuranceClaimStatus } from '../InsuranceClaims/types';
import { INSURANCE_CLAIM_STATUS_OPTIONS } from '../InsuranceClaims/fields';
import NoPecChip from './NoPecChip';

// SHER-474: server-driven pagination contract. When `serverPagination` is true the
// parent has already fetched ONE page (`bills`) plus the full `total`; this component
// stops slicing/filtering the list locally and just renders the page + a server-sized
// pager. The in-table patient-name search is hidden in this mode — name search is the
// patient-picker flow (find-a-patient). status/type/date filters are reported up via
// `onServerFilterChange` so the parent refetches. `loading`/`error` drive the placeholder
// states. All these props are optional, so existing (client-mode) callers are unchanged.
type ServerFilterState = {
    statusPaidFilter: BillStatus[];
    documentStatusFilter: DocumentStatus[];
    typeFilter: DocumentType[];
    insuranceStatusFilter: InsuranceClaimStatus[];
    selectedInsurance: string;
    startDate: string;
    endDate: string;
};

type BillsTableProps = {
    bills: any[];
    patients: any[];
    insurances: IInsurance[];
    languageCode: string;
    hideInsuranceFilter?: boolean;
    hideInsuranceStatusFilter?: boolean;
    currency: string;
    hasBudgets: boolean;
    hideSearchPatient?: boolean;
    canCreateBugdet: boolean;
    canCancelDocument?: boolean;
    makeActionBillCallBack: (index:number, action:BillActions) => void;
    // SHER-474 server-mode (all optional)
    serverPagination?: boolean;
    total?: number;
    loading?: boolean;
    error?: string | null;
    page?: number;
    rowsPerPage?: number;
    onServerPageChange?: (page:number, rowsPerPage:number) => void;
    onServerFilterChange?: (filters:ServerFilterState) => void;
};

const BillsTable: React.FC<BillsTableProps> = ({ bills, patients, languageCode, currency, hideInsuranceFilter, hideInsuranceStatusFilter, hideSearchPatient, insurances, canCreateBugdet, canCancelDocument,
                                                    hasBudgets, makeActionBillCallBack,
                                                    serverPagination, total, loading, error, page: serverPage, rowsPerPage: serverRowsPerPage, onServerPageChange, onServerFilterChange }) => {
    const [patientName, setPatientName] = useState<string>("");
    const [statusPaidFilter, setStatusPaidFilter] = useState<BillStatus[]>([]);
    const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatus[]>([]);
    const [typeFilter, setTypeFilter] = useState<DocumentType[]>([]);
    const [insuranceStatusFilter, setInsuranceStatusFilter] = useState<InsuranceClaimStatus[]>([]);
    const [selectedInsurance, setSelectedInsurance] = useState<string>("");
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [currentPageTable, setCurrentPageTable] = useState<number>(0);
  
    const history = useHistory();

    useEffect(() => {
        setCurrentPageTable(0);
    }, [patientName, statusPaidFilter, documentStatusFilter, typeFilter, selectedInsurance, insuranceStatusFilter, startDate, endDate]);

    // SHER-474: in server mode, propagate filter changes up so the parent refetches the
    // (filtered) first page. Patient-name search is excluded on purpose — it's the
    // patient-picker flow, not a list filter.
    useEffect(() => {
        if (serverPagination && typeof onServerFilterChange === 'function') {
            onServerFilterChange({ statusPaidFilter, documentStatusFilter, typeFilter, insuranceStatusFilter, selectedInsurance, startDate, endDate });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverPagination, statusPaidFilter, documentStatusFilter, typeFilter, insuranceStatusFilter, selectedInsurance, startDate, endDate]);

    // Get unique insurance IDs from patients
    const uniqueInsurance:IInsurance[] = useMemo(() => {
        // SHER-474: in server mode `bills` is only the current page, so deriving the
        // insurance options from it would hide any insurance not present on page 1 — and
        // insurance IS a server-side filter (insuranceCodes). Offer the full investigation
        // insurance list instead.
        if (serverPagination) {
            return insurances || [];
        }
        const insuranceMap = new Map<string, IInsurance>();
        bills.forEach(bill => {
            if (bill.insuranceCode && insurances && !insuranceMap.has(bill.insuranceCode)) {
                const insurance = insurances.find((insurance) => insurance.code === bill.insuranceCode);
                if(insurance){
                    insuranceMap.set(bill.insuranceCode, insurance);
                }
            }
        });
        return Array.from(insuranceMap.values());
    }, [bills, insurances, serverPagination]);

    // Create options from insurance data
    const insuranceOptions = useMemo(() => {
        const options: { value: string; label: string; }[] = [];
        uniqueInsurance.forEach((insurance, index) => {
            
            options.push({
                value: insurance.code,
                label: insurance.name
            });
            
        });
        return options;
    }, [uniqueInsurance]);

    function changePageCallback(currentPage:number){
        setCurrentPageTable(currentPage);
    }

    const billsPatients = useMemo(() => {
        // Sort bills by createdAt descending (newest first)
        const sortedBills = [...bills].sort((a, b) => 
            stringDatePostgresToDate(b.createdAt).getTime() - stringDatePostgresToDate(a.createdAt).getTime()
        );
        return sortedBills.map((bill, indexBill) => {
            console.log(bill);
            const patient = patients.find((patient) => patient.uuid === bill.uuidPatient);
            // SHER-1152: the tab's patient list can lag the server, so the patient may be missing.
            const patientName = patient ? patientFullName(patient.personalData) : "";
            const totalPatient = bill.totalGross - bill.totalDiscount;
            const externalPaid = (bill.insuranceClaims || []).reduce((acc: number, claim: any) => acc + claim.requestedAmount, 0);
            const isPaid = (totalPatient - bill.totalPaid - externalPaid) <= 0;
            // SHER-423: a document is cancellable only when fully unpaid — no patient payment
            // and no approved insurance claim. Mirrors the API eligibility guard (the API is
            // the source of truth; this only gates the icon's visibility).
            const approvedExternal = (bill.insuranceClaims || []).reduce((acc: number, claim: any) => acc + (claim.approvedAmount || 0), 0);
            const isUnpaid = (bill.totalPaid || 0) === 0 && approvedExternal === 0;
            
            // Get insurance claim statuses for this bill
            const insuranceClaimStatuses = bill.insuranceClaims?.map((claim: any) => claim.status) || [];
            
            return {
                "idInvestigation" : bill.documentNumber ? bill.documentNumber.toString() : "",
                "id" : indexBill,
                "uuid" : bill.uuid,
                "uuidPatient": bill.uuidPatient,
                "type" : bill.type,
                "insuranceCode" : bill.insuranceCode,
                "health_id" : patient?.personalData.automated_health_id ? patient?.personalData.automated_health_id : patient?.personalData.health_id,
                "patientName" :patientName, 
                "patient" : <><ColourButton size='small' icon={documentTypeToIcon(bill.type)} rgbcolor={documentTypeToColor(bill.type)} label={null}/> {patientName}</>, 
                "total" : formatCurrency(totalPatient, currency, languageCode),
                "status" : renderStatus(bill.status, bill.type, hasBudgets, isPaid ? BillStatus.PAID : BillStatus.PENDING_PAYMENT, bill.pecMissing),
                "statusValue" : bill.status,
                "isUnpaid" : isUnpaid,
                "paidStatus" : (bill.type === DocumentType.INVOICE && bill.status === DocumentStatus.CLOSED) ? isPaid ? BillStatus.PAID : BillStatus.PENDING_PAYMENT : null,
                "insuranceClaimStatuses" : insuranceClaimStatuses,
                "dateCreation" : fullDateFromPostgresString(languageCode, bill.createdAt),
                "dateCreationRaw" : stringDatePostgresToDate(bill.createdAt)
            }
        })}, [bills, patients, languageCode]);
    
    function callbackNameTyped(name:string){
        setPatientName(name.trim());
    }

    function renderStatus(billStatus:DocumentStatus, billType:DocumentType, hasBudgets:boolean, paidStatus:BillStatus, pecMissing?: boolean | null){
        let listChips = [];
        if(billStatus === DocumentStatus.DRAFT){
            listChips.push(<ColourButton icon={documentStatusToIcon(billStatus)} rgbcolor={documentStatusToColor(billStatus)} label=""/>);
        }
        else{
            listChips.push(<ColourButton icon={documentStatusToIcon(billStatus)} rgbcolor={documentStatusToColor(billStatus)} label="" />);
        }
        
        if(billStatus === DocumentStatus.CLOSED && billType === DocumentType.INVOICE){
            listChips.push(<ColourButton icon={paidStatusToIcon(paidStatus)} rgbcolor={paidStatusToColor(paidStatus)} label=""  /> );
        }
        // SHER-934: informational only — does not block pay/close/create PEC.
        listChips.push(<NoPecChip key="no-pec" pecMissing={pecMissing} />);
        
        return listChips;
    }
    const billsHeadCells = [
            { id: "idInvestigation", alignment: "left", label: <Translate id={`hospital.billing.bill.bill_id`} /> },
            // SHER-371: this column shows each patient's NDM (automated_health_id),
            // so the header is fixed to the NDM label. It used to derive the label
            // from getHealthIDLabel(patients[0]), which mislabelled the column as
            // "Dossier CHOM" whenever the first patient happened to lack an NDM.
            { id: "health_id", alignment: "left", label: <Translate id={`investigation.create.personal_data.fields.automated_health_id`} /> },
            { id: "patient", alignment: "left", label: <Translate id={`hospital.billing.bill.patient`} /> },
            { id: "total", alignment: "left", label: [<Translate id={`hospital.billing.bill.total`} />,"("+currency+")"] },
            { id: "status", alignment: "left", label: [<Translate id={`hospital.billing.bill.status.label`} />]},
            { id: "dateCreation" , alignment: "right",  label: [<Translate id={`hospital.billing.bill.date`} />] }
    ];

    // The pdf-preview + cancel actions, shared by both the client and server tables.
    // `currentRows` is the array EnhancedTable renders, so index lookups resolve against it.
    function billActions(currentRows:any[]){
        return [{"type" : "pdf", "check" :(bill:any) => bill.statusValue === DocumentStatus.CLOSED,
                    "func" : (index:number) => {
                        const findBill = currentRows.find((bill) => bill.id === index);
                        if(findBill){
                            makeActionBillCallBack(findBill.uuid, BillActions.PREVIEW)
                        }
                    }
            },
            // SHER-423: cancel/annul action — visible only for unpaid, not-yet-cancelled
            // documents and only when the user holds CANCEL_BILLING_DOCUMENT.
            {"type" : "delete",
             "tooltip" : <Translate id="hospital.billing.bill.cancel.action" />,
             "check" : (bill:any) => Boolean(canCancelDocument) && bill.isUnpaid && bill.statusValue !== DocumentStatus.CANCELLED,
             "func" : (index:number) => {
                        const findBill = currentRows.find((bill) => bill.id === index);
                        if(findBill){
                            makeActionBillCallBack(findBill.uuid, BillActions.CANCEL)
                        }
                    }
            }];
    }

    function selectBillRow(currentRows:any[], index:number){
        const findBill = currentRows.find((bill) => bill.id === index);
        if(findBill){
            history.push(HOSPITAL_BILLING_VIEW_DOCUMENT.replace(":uuidDocument", findBill.uuid))
        }
    }

    // SHER-474: server-driven mode. `bills` is already the current page and `total` is the
    // server-side full count, so we render the page as-is and let the footer page through
    // the server. No client-side name/status filtering here — the parent owns that.
    function renderServerCore(){
        if(loading){
            return <div style={{display:'flex', justifyContent:'center', padding:'2rem'}}><CircularProgress /></div>;
        }
        if(error){
            return <div style={{padding:'2rem', textAlign:'center'}}><Typography color="error">{error}</Typography></div>;
        }
        const rows = billsPatients;
        if(rows.length === 0){
            // SHER-474: distinguish "no bills at all" from "no bills match the active filters"
            // (the latter is common in server mode against a large dataset).
            const hasActiveServerFilters = documentStatusFilter.length > 0 || typeFilter.length > 0 || Boolean(selectedInsurance) || Boolean(startDate) || Boolean(endDate);
            return <div style={{padding:'2rem', textAlign:'center'}}><Typography color="textSecondary"><Translate id={hasActiveServerFilters ? "general.no_results" : "hospital.billing.no_bills"} /></Typography></div>;
        }
        return <EnhancedTable noHeader headCells={billsHeadCells} rows={rows} noSelectable
                    selectRow={(index:number) => selectBillRow(rows, index)}
                    serverPagination
                    totalCount={typeof total === 'number' ? total : rows.length}
                    currentPage={typeof serverPage === 'number' ? serverPage : 0}
                    rowsPerPage={serverRowsPerPage}
                    onServerPageChange={onServerPageChange}
                    actions={billActions(rows)}
                />;
    }

    function renderCore(){
        if(serverPagination){
            return renderServerCore();
        }
        const rows = billsPatients.filter((bill) => {
            let shouldBeFiltered = patientName.length === 0;
            const activeNameFilter = patientName.length > 0;
            let matchNameFilter = false;
            if(activeNameFilter){
                matchNameFilter = bill.patientName.toLocaleLowerCase().includes(patientName.toLocaleLowerCase()) || bill.health_id?.toLocaleLowerCase().includes(patientName.toLocaleLowerCase());
                shouldBeFiltered = matchNameFilter;
            }
            if(statusPaidFilter.length > 0){
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && (bill.paidStatus !== null ? statusPaidFilter.includes(bill.paidStatus) : false) : (bill.paidStatus !== null ? statusPaidFilter.includes(bill.paidStatus) : false));
            }
            if(typeFilter.length > 0){
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && typeFilter.includes(bill.type) : typeFilter.includes(bill.type));
            }
            if(documentStatusFilter.length > 0){
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && documentStatusFilter.includes(bill.statusValue) : documentStatusFilter.includes(bill.statusValue));
            }
            if(selectedInsurance) {
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && bill.insuranceCode === selectedInsurance : bill.insuranceCode === selectedInsurance);
            }
            if(insuranceStatusFilter.length > 0){
                // Check if any insurance claim status matches the filter
                const hasMatchingInsuranceStatus = bill.insuranceClaimStatuses && bill.insuranceClaimStatuses.some((status: InsuranceClaimStatus) => 
                    insuranceStatusFilter.includes(status)
                );
                shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && hasMatchingInsuranceStatus : hasMatchingInsuranceStatus);
            }
            
            // Date range filtering
            if (startDate || endDate) {
                const billDate = bill.dateCreationRaw;
                const startDateTime = startDate ? new Date(startDate + 'T00:00:00') : null;
                const endDateTime = endDate ? new Date(endDate + 'T23:59:59') : null;
                
                if (startDateTime && endDateTime) {
                    shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && billDate >= startDateTime && billDate <= endDateTime : billDate >= startDateTime && billDate <= endDateTime);
                } else if (startDateTime) {
                    shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && billDate >= startDateTime : billDate >= startDateTime);
                } else if (endDateTime) {
                    shouldBeFiltered = shouldBeFiltered && (activeNameFilter ? matchNameFilter && billDate <= endDateTime : billDate <= endDateTime);
                }
            }
            
            return shouldBeFiltered;
        })

        return <EnhancedTable noHeader headCells={billsHeadCells} rows={rows}  noSelectable
                    selectRow={(index:number) => selectBillRow(rows, index)}
                    currentPage={currentPageTable}
                    changePageCallback={changePageCallback}
                    actions={billActions(rows)}
                    />
    }

    function applyFilterGeneral(filter:number[] , functionAdd: (value:number[]) => void, value:number){
        if(filter && filter.includes(value)){
            if(filter.length === 1){
                functionAdd([]);
            }
            else{
                functionAdd(filter.filter((s) => s !== value));
            }
        }
        else if(filter){
            functionAdd([...filter, value]);
        }
        else{
            functionAdd([value]);
        }
    }

    function applyDocumentStatus(status:DocumentStatus){
        applyFilterGeneral(documentStatusFilter, setDocumentStatusFilter, status);
    }

    function applyTypeStatusFilter(type:DocumentType){
        applyFilterGeneral(typeFilter, setTypeFilter, type);
    }

    function applyStatusPaidFilter(status:BillStatus){
        applyFilterGeneral(statusPaidFilter, setStatusPaidFilter, status);
    }

    function applyInsuranceStatusFilter(status:InsuranceClaimStatus){
        if(insuranceStatusFilter && insuranceStatusFilter.includes(status)){
            if(insuranceStatusFilter.length === 1){
                setInsuranceStatusFilter([]);
            }
            else{
                setInsuranceStatusFilter(insuranceStatusFilter.filter((s) => s !== status));
            }
        }
        else if(insuranceStatusFilter){
            setInsuranceStatusFilter([...insuranceStatusFilter, status]);
        }
        else{
            setInsuranceStatusFilter([status]);
        }
    }

    // Insurance and Date filters component - all on the same line
    const renderDateFilters = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Insurance Select */}
            {!hideInsuranceFilter && insuranceOptions.length > 0 && (
                <FormControl variant="outlined" margin="dense" style={{ minWidth: '200px', maxWidth: '220px' }}>
                    <InputLabel id="insurance-filter-label">
                        <Translate id="investigation.create.personal_data.fields.insurance" />
                    </InputLabel>
                    <Select
                        labelId="insurance-filter-label"
                        value={selectedInsurance}
                        onChange={(e) => setSelectedInsurance(e.target.value)}
                        label={<Translate id="investigation.create.personal_data.fields.insurance" />}
                    >
                        <MenuItem value="">
                            <Translate id="hospital.billing.all_insurances" />
                        </MenuItem>
                        {insuranceOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {/* Start Date */}
            <FormControl variant="outlined" margin="dense" style={{ maxWidth: '220px' }}>
                <DesktopDatePicker
                    label={<Translate id="hospital.search_box.start_date" />}
                    value={startDate ? dayjs(startDate) : null}
                    onChange={(newDate) => setStartDate(newDate ? newDate.format('YYYY-MM-DD') : '')}
                    slotProps={{
                        textField: {
                            variant: 'outlined',
                            fullWidth: true
                        }
                    }}
                />
            </FormControl>
            {/* End Date */}
            <FormControl variant="outlined" margin="dense" style={{ maxWidth: '220px' }}>
                <DesktopDatePicker
                    label={<Translate id="hospital.search_box.end_date" />}
                    value={endDate ? dayjs(endDate) : null}
                    onChange={(newDate) => setEndDate(newDate ? newDate.format('YYYY-MM-DD') : '')}
                    slotProps={{
                        textField: {
                            variant: 'outlined',
                            fullWidth: true
                        }
                    }}
                />
            </FormControl>
            {/* Clear Dates Button */}
            {(startDate || endDate) && (
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                        setStartDate("");
                        setEndDate("");
                    }}
                    style={{ 
                        height: '40px', // Match the height of the select dropdowns
                        minWidth: '100px',
                        textTransform: 'none'
                    }}
                >
                    <Translate id="hospital.search_box.clear_dates" />
                </Button>
            )}
        </div>
    );

    let filterItems:FilterItem[] = [
        {label:"draft", selected: documentStatusFilter.includes(DocumentStatus.DRAFT),  value:DocumentStatus.DRAFT, icon:documentStatusToIcon(DocumentStatus.DRAFT), color:documentStatusToColor(DocumentStatus.DRAFT), callBack:() => applyDocumentStatus(DocumentStatus.DRAFT)},
        {label:"closed", selected: documentStatusFilter.includes(DocumentStatus.CLOSED),  value:DocumentStatus.CLOSED, icon:documentStatusToIcon(DocumentStatus.CLOSED), color:documentStatusToColor(DocumentStatus.CLOSED), callBack:() => applyDocumentStatus(DocumentStatus.CLOSED)},
        // SHER-474: paid/pending is a DERIVED status (computed from totals+claims), not
        // server-filterable yet — hide it in server mode so the chip isn't inert (deferred).
        ...(serverPagination ? [] : [
            {label:"paid", selected: statusPaidFilter.includes(BillStatus.PAID), value:BillStatus.PAID, icon:paidStatusToIcon(BillStatus.PAID), color:paidStatusToColor(BillStatus.PAID), callBack:() => applyStatusPaidFilter(BillStatus.PAID)},
            {label:"pending_payment", selected: statusPaidFilter.includes(BillStatus.PENDING_PAYMENT),  value:BillStatus.PENDING_PAYMENT, icon:paidStatusToIcon(BillStatus.PENDING_PAYMENT), color:paidStatusToColor(BillStatus.PENDING_PAYMENT), callBack:() => applyStatusPaidFilter(BillStatus.PENDING_PAYMENT)},
        ]),
    ];

    // Create separate PEC (insurance) status filters
    let pecFilterItems:FilterItem[] = [];
    if (!hideInsuranceStatusFilter) {
        pecFilterItems = INSURANCE_CLAIM_STATUS_OPTIONS.map((option, index) => ({
            label: option.label, // Use the same translation key as the form
            selected: insuranceStatusFilter.includes(option.value as InsuranceClaimStatus),
            value: index,
            icon: insuranceClaimStatusToIcon(option.value as InsuranceClaimStatus),
            color: insuranceClaimStatusToColor(option.value as InsuranceClaimStatus),
            callBack: () => applyInsuranceStatusFilter(option.value as InsuranceClaimStatus)
        }));
    }
    if(canCreateBugdet){
        const bugetFilters:FilterItem[] = [
            {label:"summaries", selected: typeFilter.includes(DocumentType.SUMMARY),  value:DocumentType.INVOICE, icon:documentTypeToIcon(DocumentType.SUMMARY), color:documentTypeToColor(DocumentType.SUMMARY), callBack:() => applyTypeStatusFilter(DocumentType.SUMMARY)},
            {label:"budget", selected: typeFilter.includes(DocumentType.BUDGET), value:DocumentType.BUDGET, icon:documentTypeToIcon(DocumentType.BUDGET), color:documentTypeToColor(DocumentType.BUDGET), callBack:() => applyTypeStatusFilter(DocumentType.BUDGET)},
            {label:"invoices", selected: typeFilter.includes(DocumentType.INVOICE),  value:DocumentType.INVOICE, icon:documentTypeToIcon(DocumentType.INVOICE), color:documentTypeToColor(DocumentType.INVOICE), callBack:() => applyTypeStatusFilter(DocumentType.INVOICE)},                         
        ];
        filterItems = [...bugetFilters, ...filterItems];
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <SearchBox
                    hideSearchPatient={hideSearchPatient || serverPagination}
                    textField={{label:"hospital.billing.search_patient", callBack:callbackNameTyped}}
                    filterItems={filterItems}
                    extraFilters={renderDateFilters}
                />
            </Grid>
            
            {/* Separate PEC (Insurance) Filters */}
            {/* SHER-474: PEC (insurance-claim status) is a join-based derived filter, not
                server-side yet — hidden in server mode (deferred). */}
            {!serverPagination && !hideInsuranceStatusFilter && pecFilterItems.length > 0 && (
                <Grid item xs={12}>
                    <Paper elevation={3} style={{padding:"1rem", marginTop:'0.5rem'}} >
                        <Grid container spacing={2}>
                            <Grid item xs={12} container alignItems='center'>
                                <Grid item zeroMinWidth>
                                    <Typography variant="body2" component="span">
                                        <Translate id="hospital.search_box.pec_filter" />
                                    </Typography>:
                                    {
                                        pecFilterItems.map((item) => {
                                            return(
                                                <ChipContainer key={item.value}>
                                                    <ColourChip 
                                                        rgbcolor={item.color} 
                                                        active={!item.selected} 
                                                        avatar={item.icon}
                                                        onClick={item.callBack} 
                                                        label={<Translate id={item.label} />}
                                                    />
                                                </ChipContainer>
                                            );
                                        })
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            )}
            

            
            <Grid item xs={12}>
                {
                    renderCore()
                }
            </Grid>
        </Grid>
    );
};

export default BillsTable;
